import { useState, useEffect, useCallback } from 'react';
import { Card, Button, Input } from '@/components/ui';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { tbApi } from '@/lib/tbApi';
import { useTelemetry } from '@/hooks/useTelemetry';
import {
  Clock, Zap, Plus, Trash2, Power, AlertTriangle, RefreshCw, DownloadCloud, Wifi,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AutoScheduleTabProps {
  project: string;
  gh: string;
  isReady: boolean;
  isOnline: boolean;
  userRole: string;
}

interface ControlEntry {
  controlKey: string;
  nameTh: string;
}

interface Schedule {
  id: number;
  control_key: string;
  name: string;
  time_on: string | null;
  time_off: string | null;
  days_of_week: number[];
  is_active: number;
}

interface RuleCondition { sensor_key: string; operator: 'gt' | 'lt' | 'gte' | 'lte' | 'eq'; value: number; }
interface RuleAction { control_key: string; value: number }
interface AutomationRule {
  id: number;
  name: string;
  conditions: RuleCondition[];
  actions: RuleAction[];
  is_active: number;
  last_triggered_at: string | null;
  trigger_count: number;
}

interface SensorEntry { sensorKey: string; dataKey: string; unit: string; }

const DAY_LABELS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const OPERATOR_LABELS: Record<string, string> = { gt: 'มากกว่า', lt: 'น้อยกว่า', gte: 'มากกว่าหรือเท่ากับ', lte: 'น้อยกว่าหรือเท่ากับ', eq: 'เท่ากับ' };

export function AutoScheduleTab({ project, gh, isReady, isOnline, userRole }: AutoScheduleTabProps) {
  const { addToast } = useToast();
  const canControl = userRole === 'superadmin' || userRole === 'admin' || userRole === 'operator';

  const [controls, setControls] = useState<ControlEntry[]>([]);
  const [sensors, setSensors] = useState<SensorEntry[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [otaState, setOtaState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // relay board publishes these on its own (SPEC.md §3) — no extra firmware needed
  const { data: deviceInfo } = useTelemetry({
    project, gh, keys: ['firmware_version', 'wifi_ssid', 'rssi'], pollInterval: 5 * 60 * 1000,
  });
  const fwVersion = deviceInfo.firmware_version as string | undefined;
  const wifiSsid = deviceInfo.wifi_ssid as string | undefined;
  const rssi = deviceInfo.rssi as number | undefined;

  const handleCheckOta = useCallback(async () => {
    setOtaState('sending');
    try {
      await tbApi.sendRpc(project, gh, 'checkOta', {});
      setOtaState('sent');
    } catch (e) {
      console.error(e);
      setOtaState('error');
    }
  }, [project, gh]);

  const loadAll = useCallback(async () => {
    if (!project || !gh) return;
    setIsLoading(true);
    try {
      const [controlsRes, sensorsRes, schedulesRes, rulesRes] = await Promise.all([
        api.get<{ controls: any[] }>(`/projects/${project}/greenhouses/${gh}/control-configs`),
        api.get<{ sensors: SensorEntry[] }>(`/projects/${project}/greenhouses/${gh}/sensor-configs`),
        api.get<{ schedules: Schedule[] }>(`/projects/${project}/greenhouses/${gh}/schedules`),
        api.get<{ rules: AutomationRule[] }>(`/projects/${project}/greenhouses/${gh}/automation-rules`),
      ]);
      if (controlsRes.success) setControls((controlsRes.data?.controls ?? []).map((c: any) => ({ controlKey: c.control_key, nameTh: c.name_th })));
      if (sensorsRes.success) setSensors(sensorsRes.data?.sensors ?? []);
      if (schedulesRes.success) setSchedules(schedulesRes.data?.schedules ?? []);
      if (rulesRes.success) setRules(rulesRes.data?.rules ?? []);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  }, [project, gh]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const controlName = (key: string) => controls.find((c) => c.controlKey === key)?.nameTh ?? key;

  const toggleSchedule = async (id: number) => {
    try {
      await api.put(`/projects/${project}/greenhouses/${gh}/schedules/${id}/toggle`, {});
      loadAll();
    } catch { addToast({ type: 'error', message: 'สลับสถานะไม่สำเร็จ' }); }
  };
  const deleteSchedule = async (id: number) => {
    if (!confirm('ลบตารางเวลานี้?')) return;
    try {
      await api.delete(`/projects/${project}/greenhouses/${gh}/schedules/${id}`);
      addToast({ type: 'success', message: 'ลบสำเร็จ' });
      loadAll();
    } catch { addToast({ type: 'error', message: 'ลบไม่สำเร็จ' }); }
  };

  const toggleRule = async (id: number) => {
    try {
      await api.put(`/projects/${project}/greenhouses/${gh}/automation-rules/${id}/toggle`, {});
      loadAll();
    } catch { addToast({ type: 'error', message: 'สลับสถานะไม่สำเร็จ' }); }
  };
  const deleteRule = async (id: number) => {
    if (!confirm('ลบกฎนี้?')) return;
    try {
      await api.delete(`/projects/${project}/greenhouses/${gh}/automation-rules/${id}`);
      addToast({ type: 'success', message: 'ลบสำเร็จ' });
      loadAll();
    } catch { addToast({ type: 'error', message: 'ลบไม่สำเร็จ' }); }
  };

  const controlsDisabled = !isReady || !isOnline || !canControl;

  return (
    <div className="space-y-6">
      {!canControl && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300">คุณไม่มีสิทธิ์ตั้งค่าระบบอัตโนมัติ (ดูได้อย่างเดียว)</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Clock className="w-4 h-4" />
          <span>ตารางเวลา + กฎเงื่อนไข ทำงานผ่านเซิร์ฟเวอร์ (เช็คทุก 1 นาที) ไม่ต้องพึ่งโหมด Auto บนตัวบอร์ด</span>
        </div>
        <button
          onClick={loadAll}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          รีเฟรช
        </button>
      </div>

      {/* System info + OTA */}
      <section>
        <Card>
          <div className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
              <span className="font-medium text-gray-900 dark:text-gray-100">Relay board</span>
              {fwVersion && <span>Firmware v{fwVersion}</span>}
              {wifiSsid && (
                <span className="inline-flex items-center gap-1"><Wifi className="w-3.5 h-3.5" />{wifiSsid}{rssi !== undefined ? ` (${rssi} dBm)` : ''}</span>
              )}
              {!fwVersion && <span className="text-gray-400 dark:text-gray-500">ยังไม่มีข้อมูล firmware_version</span>}
            </div>
            {canControl && (
              <button
                onClick={handleCheckOta}
                disabled={otaState === 'sending'}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                title="สั่งให้ relay board เช็คอัปเดต firmware จาก GitHub release ระยะไกล"
              >
                <DownloadCloud className="w-3.5 h-3.5" />
                {otaState === 'sent' ? 'ส่งคำสั่งแล้ว' : otaState === 'error' ? 'ส่งไม่สำเร็จ ลองใหม่' : 'เช็คอัปเดต Firmware'}
              </button>
            )}
          </div>
          {otaState === 'sent' && (
            <p className="px-4 pb-4 text-xs text-gray-400 dark:text-gray-500">
              ส่งคำสั่งไปยังบอร์ดแล้ว ถ้ามีเวอร์ชันใหม่บอร์ดจะดาวน์โหลดและรีสตาร์ทเอง รีเฟรชหน้านี้ภายหลังเพื่อดู Firmware version ล่าสุด
            </p>
          )}
        </Card>
      </section>

      {/* ตั้งเวลา */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <span className="w-1.5 h-5 bg-blue-500 rounded-full" />
            ตั้งเวลาเปิด/ปิด
          </h2>
          <Button size="sm" onClick={() => setShowScheduleForm(true)} disabled={controlsDisabled || controls.length === 0}>
            <Plus className="w-4 h-4" /> เพิ่มตารางเวลา
          </Button>
        </div>

        {schedules.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 py-4">ยังไม่มีตารางเวลา</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schedules.map((s) => (
              <Card key={s.id}>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100">{s.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{controlName(s.control_key)}</p>
                    </div>
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                      {s.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    เปิด {s.time_on ?? '--:--'} · ปิด {s.time_off ?? '--:--'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {s.days_of_week.length === 0 ? 'ทุกวัน' : s.days_of_week.map((d) => DAY_LABELS[d]).join(' ')}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => toggleSchedule(s.id)} disabled={controlsDisabled} className="flex-1">
                      <Power className="w-3.5 h-3.5" /> {s.is_active ? 'ปิด' : 'เปิด'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteSchedule(s.id)} disabled={controlsDisabled} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* กฎเงื่อนไข */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <span className="w-1.5 h-5 bg-purple-500 rounded-full" />
            กฎอัตโนมัติตามเซนเซอร์
          </h2>
          <Button size="sm" onClick={() => setShowRuleForm(true)} disabled={controlsDisabled || controls.length === 0 || sensors.length === 0}>
            <Plus className="w-4 h-4" /> เพิ่มกฎ
          </Button>
        </div>

        {rules.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 py-4">ยังไม่มีกฎอัตโนมัติ</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rules.map((r) => (
              <Card key={r.id}>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{r.name}</p>
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                      {r.is_active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 space-y-0.5">
                    {r.conditions.map((c, i) => (
                      <p key={i}>ถ้า <b>{c.sensor_key}</b> {OPERATOR_LABELS[c.operator]} {c.value}</p>
                    ))}
                    {r.actions.map((a, i) => (
                      <p key={i}>→ {controlName(a.control_key)} {a.value === 1 ? 'เปิด' : 'ปิด'}</p>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    ทำงานแล้ว {r.trigger_count} ครั้ง{r.last_triggered_at ? ` · ล่าสุด ${new Date(r.last_triggered_at.endsWith('Z') ? r.last_triggered_at : r.last_triggered_at + 'Z').toLocaleString('th-TH')}` : ''}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => toggleRule(r.id)} disabled={controlsDisabled} className="flex-1">
                      <Power className="w-3.5 h-3.5" /> {r.is_active ? 'ปิด' : 'เปิด'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteRule(r.id)} disabled={controlsDisabled} className="text-red-600 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {showScheduleForm && (
        <ScheduleFormModal
          project={project} gh={gh} controls={controls}
          onClose={() => setShowScheduleForm(false)}
          onSaved={() => { setShowScheduleForm(false); loadAll(); }}
        />
      )}
      {showRuleForm && (
        <RuleFormModal
          project={project} gh={gh} controls={controls} sensors={sensors}
          onClose={() => setShowRuleForm(false)}
          onSaved={() => { setShowRuleForm(false); loadAll(); }}
        />
      )}
    </div>
  );
}

function ScheduleFormModal({ project, gh, controls, onClose, onSaved }: {
  project: string; gh: string; controls: ControlEntry[]; onClose: () => void; onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [controlKey, setControlKey] = useState(controls[0]?.controlKey ?? '');
  const [name, setName] = useState('');
  const [timeOn, setTimeOn] = useState('');
  const [timeOff, setTimeOff] = useState('');
  const [days, setDays] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const toggleDay = (d: number) => setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !controlKey) { addToast({ type: 'error', message: 'กรอกชื่อและเลือกอุปกรณ์ก่อน' }); return; }
    if (!timeOn && !timeOff) { addToast({ type: 'error', message: 'ตั้งเวลาเปิดหรือปิดอย่างน้อย 1 อย่าง' }); return; }
    setIsLoading(true);
    try {
      await api.post(`/projects/${project}/greenhouses/${gh}/schedules`, {
        control_key: controlKey, name, schedule_type: 'daily',
        time_on: timeOn || null, time_off: timeOff || null, days_of_week: days,
      });
      addToast({ type: 'success', message: 'สร้างตารางเวลาสำเร็จ' });
      onSaved();
    } catch { addToast({ type: 'error', message: 'สร้างไม่สำเร็จ' }); }
    finally { setIsLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">เพิ่มตารางเวลา</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">อุปกรณ์</label>
              <select value={controlKey} onChange={(e) => setControlKey(e.target.value)} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100">
                {controls.map((c) => <option key={c.controlKey} value={c.controlKey}>{c.nameTh}</option>)}
              </select>
            </div>
            <Input label="ชื่อตารางเวลา" value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น เปิดพัดลมตอนกลางวัน" required />
            <div className="grid grid-cols-2 gap-4">
              <Input label="เวลาเปิด" type="time" value={timeOn} onChange={(e) => setTimeOn(e.target.value)} />
              <Input label="เวลาปิด" type="time" value={timeOff} onChange={(e) => setTimeOff(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">วันที่ทำงาน (ไม่เลือก = ทุกวัน)</label>
              <div className="flex gap-1.5 flex-wrap">
                {DAY_LABELS.map((label, d) => (
                  <button
                    key={d} type="button" onClick={() => toggleDay(d)}
                    className={cn('w-9 h-9 rounded-lg text-sm font-medium border', days.includes(d) ? 'bg-primary text-white border-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300')}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">ยกเลิก</Button>
              <Button type="submit" isLoading={isLoading} className="flex-1">บันทึก</Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}

function RuleFormModal({ project, gh, controls, sensors, onClose, onSaved }: {
  project: string; gh: string; controls: ControlEntry[]; sensors: SensorEntry[]; onClose: () => void; onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [name, setName] = useState('');
  const [sensorKey, setSensorKey] = useState(sensors[0]?.dataKey ?? '');
  const [operator, setOperator] = useState<RuleCondition['operator']>('gt');
  const [threshold, setThreshold] = useState('');
  const [controlKey, setControlKey] = useState(controls[0]?.controlKey ?? '');
  const [action, setAction] = useState<'1' | '0'>('1');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !sensorKey || threshold === '' || !controlKey) { addToast({ type: 'error', message: 'กรอกข้อมูลให้ครบ' }); return; }
    setIsLoading(true);
    try {
      await api.post(`/projects/${project}/greenhouses/${gh}/automation-rules`, {
        name, trigger_type: 'sensor',
        conditions: [{ sensor_key: sensorKey, operator, value: Number(threshold) }],
        actions: [{ control_key: controlKey, value: Number(action) }],
      });
      addToast({ type: 'success', message: 'สร้างกฎสำเร็จ' });
      onSaved();
    } catch { addToast({ type: 'error', message: 'สร้างไม่สำเร็จ' }); }
    finally { setIsLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">เพิ่มกฎอัตโนมัติ</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input label="ชื่อกฎ" value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น เปิดพัดลมเมื่อร้อน" required />

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 space-y-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">ถ้า (เงื่อนไข)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">เซนเซอร์</label>
                  <select value={sensorKey} onChange={(e) => setSensorKey(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">
                    {sensors.map((s) => <option key={s.dataKey} value={s.dataKey}>{s.dataKey}{s.unit ? ` (${s.unit})` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">เงื่อนไข</label>
                  <select value={operator} onChange={(e) => setOperator(e.target.value as RuleCondition['operator'])} className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">
                    {Object.entries(OPERATOR_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </div>
              </div>
              <Input label="ค่า" type="number" step="any" value={threshold} onChange={(e) => setThreshold(e.target.value)} required />
            </div>

            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 space-y-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">ให้ทำ (คำสั่ง)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">อุปกรณ์</label>
                  <select value={controlKey} onChange={(e) => setControlKey(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">
                    {controls.map((c) => <option key={c.controlKey} value={c.controlKey}>{c.nameTh}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">สถานะ</label>
                  <select value={action} onChange={(e) => setAction(e.target.value as '1' | '0')} className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">
                    <option value="1">เปิด</option>
                    <option value="0">ปิด</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">ยกเลิก</Button>
              <Button type="submit" isLoading={isLoading} className="flex-1">บันทึก</Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
